import { createReducer } from 'typesafe-actions';

import { DEFAULT_BOOKINGS_TAB } from 'components/bookings-tabs';
import * as actions from './actions';
import { BookingsTabState } from './types';

export const getInitialState = (): BookingsTabState => ({
  activeTab: DEFAULT_BOOKINGS_TAB,
  hasLocalChoice: false,
  /*
   * ⭐ PENDING BEFORE ANYTHING HAS BEEN ASKED, and that is the point rather than an approximation.
   * The ask lives in an effect gated on the SDK client, so there is a window where the question has
   * not gone out yet -- and a flag that only turned true once it had would be false exactly when
   * the page is making its first render decisions. Starting pending makes the window safe; the
   * timeout in `useSyncBookingsTabWithHostApp` is what stops it being permanent.
   */
  isHostAnswerPending: true,
});

export const initialState: BookingsTabState = getInitialState();

const setBookingsTabHandler = (
  state: BookingsTabState,
  { payload }: ReturnType<typeof actions.setBookingsTab>,
): BookingsTabState => ({ ...state, activeTab: payload, hasLocalChoice: true, isHostAnswerPending: false });

/**
 * ⛔ THE TENANT WINS. A stored tab that arrives after they have pressed one themselves is an answer
 * to a question that stopped being current, and applying it would move the page under them.
 *
 * A `null` payload -- nothing stored, expired, or unrecognised -- leaves the default standing
 * rather than overwriting it, so a host that never answers and a host that answers "nothing" land
 * in the same place.
 *
 * It does NOT mark the slice as locally chosen: hydration is the host talking, not the tenant.
 *
 * ⚠️ IT DOES, HOWEVER, ALWAYS END THE WAIT -- on every arm, including the ones that change nothing.
 * "The host says nothing" and "the tenant already moved" are both answers; only "still asking" is
 * not, and a page held back on a question that has been settled would never open a booking at all.
 *
 * ⭐ AND THE WAIT ENDS ONCE. The question goes out exactly once per load, so a SECOND answer is an
 * answer to nothing -- and the only thing that produces one is the hazard itself: the deadline in
 * `useSyncBookingsTabWithHostApp` fires `null`, the pane takes that as leave to pick a booking out
 * of the tab on screen, and the host's real answer then arrives late and moves the tab out from
 * under the booking it just opened. `hasLocalChoice` does not catch that one -- nobody pressed
 * anything, which is exactly why the pick happened. So a hydration arriving on a settled slice is
 * dropped on its own, rather than relying on the tenant having beaten it.
 */
const hydrateBookingsTabHandler = (
  state: BookingsTabState,
  { payload }: ReturnType<typeof actions.hydrateBookingsTab>,
): BookingsTabState =>
  state.hasLocalChoice || !state.isHostAnswerPending || !payload
    ? { ...state, isHostAnswerPending: false }
    : { ...state, activeTab: payload, isHostAnswerPending: false };

const bookingsTabReducer = createReducer(initialState)
  .handleAction(actions.setBookingsTab, setBookingsTabHandler)
  .handleAction(actions.hydrateBookingsTab, hydrateBookingsTabHandler);

export default bookingsTabReducer;
